/**
 * What the SPA does with the feature-flag list the session carries (B10).
 *
 * A DECISION MODULE, like `authFailure.ts` and `capabilities.ts`: the rules live
 * here so they can be tested without a store, a component or a router in the
 * room, and so the two places that consume them — the auth store and the axios
 * interceptor — cannot drift into two different answers.
 *
 * ⚠️ NONE OF THIS IS AUTHORISATION. Every flagged surface is refused by the API
 * whatever the SPA renders (`api/auth/features.py::require_feature`), and the
 * pond list drops its thermal fields server-side. What this decides is what a
 * customer is OFFERED — the difference between "you did not buy this" and "this
 * is broken", which is the whole point of the card.
 */

/** The product modules a tenant either bought or did not. */
export type FeatureFlag = 'heatmap' | 'thermal' | 'reports' | 'weather'

/** Every flag, in catalogue order — mirrors `db/features.py`. */
export const FEATURE_FLAGS: readonly FeatureFlag[] = [
  'heatmap',
  'thermal',
  'reports',
  'weather',
]

/**
 * What an operator reads on the switch, and what the switch actually sells.
 *
 * HERE RATHER THAN IN THE PANEL COMPONENT, so the wording sits next to the
 * catalogue it describes instead of drifting from it in a template nobody
 * rereads. The `sells` line is not decoration — it is where an operator learns
 * what a switch takes away before a customer does.
 *
 * ⚠️ `weather` IS THE ONLY ONE THAT STARTS OFF. The other three default to on
 * because they shipped before the flag mechanism existed; weather is a module
 * that has to be granted. An operator opening a fresh tenant's panel will see
 * it dark, and that is correct rather than a missing row.
 */
export const FEATURE_LABELS: Record<FeatureFlag, { label: string; sells: string }> = {
  heatmap: {
    label: 'Heatmap',
    sells: 'Indicator heatmap over the map. The most expensive module — every render pulls fresh satellite imagery.',
  },
  thermal: {
    label: 'Thermal',
    sells: 'Surface temperature: the LST series and the thermal card. Nightly satellite ingest stops too. Wind is NOT included — it has its own feed and stays on.',
  },
  reports: {
    label: 'Reports',
    sells: 'PDF export of the selected indicators for a date range.',
  },
  weather: {
    label: 'Weather',
    sells: 'Wind and air-temperature history on the chart, plus the 48 h forecast tile. Off until granted — this module is not part of Thermal.',
  },
}

/**
 * Whether a session that reports `features` has `flag`.
 *
 * ABSENT MEANS NO, and an EMPTY list means no as well. That is deliberate and it
 * is the fail-closed direction for a UI: the server fills this field on every
 * session response, so an empty one can only mean a session object the SPA built
 * itself. Offering everything in that case would show controls the API refuses;
 * offering nothing shows a smaller product until the next boot probe, which is
 * recoverable and quiet.
 */
export function hasFeature(features: readonly string[], flag: FeatureFlag): boolean {
  return features.includes(flag)
}

/**
 * Whether a failed request should make the SPA re-read its session.
 *
 * WHY ANY 403 AND NOT ONLY A FEATURE ONE. The refusal a stale tab gets is
 * indistinguishable from any other 403 at this layer: the reason the backend
 * records (`feature_disabled`) goes to the audit row, not to the response body,
 * so matching on it would mean matching on human-readable prose. Re-reading the
 * session on every 403 costs one cheap request on a path that is already an
 * error, and it fixes the case this card cares about — an admin flips a flag
 * while the customer has a tab open, and the control disappears on the first
 * click instead of surviving until a reload.
 *
 * 401 IS EXCLUDED because it is already handled, and handled differently:
 * `classifyAuthFailure` wipes the session and redirects. Re-reading a session
 * that just expired would race that.
 *
 * The auth endpoints are excluded too: a 403 from /api/auth/* is about the
 * session itself, and probing the session in response would be a loop.
 */
export function shouldResyncSession(status: number | undefined, url: string): boolean {
  if (status !== 403) return false
  return !url.startsWith('/api/auth/')
}

/**
 * A split-view pair with any unavailable indicator swapped out.
 *
 * WHY THIS IS NEEDED AT ALL: the pair is PERSISTED in sessionStorage and its
 * factory default is `['ndci', 'lst']`. A customer without the thermal module
 * would open split view onto a pane pinned to a series that cannot draw, with
 * the select offering no matching option — an empty half-screen and nothing
 * naming the cause. Persisted state outliving a flag is the general shape of
 * this problem; thermal is only today's instance.
 *
 * The replacement is the first ALLOWED indicator that is not already pinned to
 * the other pane, so the two panes never collapse onto one series. If nothing
 * is available the pair is returned untouched — an impossible state today (the
 * catalogue always leaves optical indicators) and not worth inventing a
 * behaviour for.
 */
export function usableSplitPair<T extends string>(
  pair: readonly [T, T],
  allowed: readonly T[],
): [T, T] {
  const substitute = (key: T, other: T): T => {
    if (allowed.includes(key)) return key
    return allowed.find((candidate) => candidate !== other) ?? key
  }
  const left = substitute(pair[0], pair[1])
  return [left, substitute(pair[1], left)]
}
